import { useState } from 'react';
import { Button, Select, DataTable, Column, Card, EmptyState } from '../../../shared/components';
import { DownloadIcon } from '../../../shared/icons';
import { getHumanResourcesRepository } from '../../../shared/services/humanResources/repositoryFactory';
import { SummaryRow } from '../../../shared/services/humanResources/types';
import { exportSummaryExcel, exportSummaryCsv } from '../utils/exportSummary';

const repo = getHumanResourcesRepository();

function formatCurrency(n: number): string {
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n);
}
function formatHours(n: number): string {
  return n.toFixed(1);
}

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

/** Calcula [dateFrom, dateTo] de una quincena (1: día 1-15, 2: día 16-fin de mes). */
function quincenaRange(year: number, month: number, quincena: 1 | 2): [string, string] {
  const pad = (n: number) => String(n).padStart(2, '0');
  if (quincena === 1) return [`${year}-${pad(month + 1)}-01`, `${year}-${pad(month + 1)}-15`];
  const last = daysInMonth(year, month);
  return [`${year}-${pad(month + 1)}-16`, `${year}-${pad(month + 1)}-${pad(last)}`];
}

/**
 * Pestaña "Resumen": equivalente a la hoja RESUMEN del Excel, pero calculada
 * EN VIVO a partir de los registros diarios (sin boton "Calcular" ni riesgo
 * de quedar desactualizada). Si la quincena elegida cruza un cambio de ley
 * (ej. 15 de julio de 2026), cada turno usa automaticamente el factor legal
 * vigente en SU propia fecha.
 *
 * Incluye exportación a Excel/CSV con el mismo layout de columnas que la
 * hoja RESUMEN original - este es el archivo que reemplaza el envío manual
 * de los 15 .xlsm de cada líder/coordinador a Contabilidad.
 */
export function SummaryTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [quincena, setQuincena] = useState<1 | 2>(now.getDate() <= 15 ? 1 : 2);
  const [rows, setRows] = useState<SummaryRow[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [range, setRange] = useState<[string, string] | null>(null);

  const calculate = async () => {
    setIsLoading(true);
    const [dateFrom, dateTo] = quincenaRange(year, month, quincena);
    setRange([dateFrom, dateTo]);
    try {
      const result = await repo.getSummary(dateFrom, dateTo);
      setRows(result);
    } finally {
      setIsLoading(false);
    }
  };

  const yearOptions = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => ({ value: String(y), label: String(y) }));
  const monthOptions = MONTHS.map((m, i) => ({ value: String(i), label: m }));
  const quincenaOptions = [{ value: '1', label: '1ra quincena (1-15)' }, { value: '2', label: '2da quincena (16-fin de mes)' }];

  const totalPay = rows?.reduce((sum, r) => sum + r.totalPay, 0) ?? 0;

  const columns: Column<SummaryRow>[] = [
    { key: 'employee', header: 'Empleado', render: (r) => r.employeeFullName, sortable: true, sortValue: (r) => r.employeeFullName },
    { key: 'temporal', header: 'Temporal', render: (r) => r.temporalCompany ?? '—' },
    { key: 'assigned', header: 'H. Asignadas', render: (r) => formatHours(r.assignedHours) },
    { key: 'operative', header: 'H. Laboradas', render: (r) => formatHours(r.operativeHours) },
    { key: 'extraHours', header: 'H. Extra', render: (r) => formatHours(r.totalExtraHours) },
    { key: 'extraPay', header: 'Valor Extra', render: (r) => formatCurrency(r.totalExtraPay) },
    { key: 'surchargeHours', header: 'H. Recargo', render: (r) => formatHours(r.totalSurchargeHours) },
    { key: 'surchargePay', header: 'Valor Recargo', render: (r) => formatCurrency(r.totalSurchargePay) },
    { key: 'festiveHours', header: 'H. Festivo', render: (r) => formatHours(r.festiveWithCompHours + r.festiveWithoutCompHours) },
    { key: 'festivePay', header: 'Valor Festivo', render: (r) => formatCurrency(r.festiveWithCompPay + r.festiveWithoutCompPay) },
    { key: 'total', header: 'Total a Pagar', render: (r) => <strong>{formatCurrency(r.totalPay)}</strong> },
  ];

  return (
    <div className="space-y-4">
      <Card>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <Select label="Año" options={yearOptions} value={String(year)} onChange={(e) => setYear(Number(e.target.value))} />
          <Select label="Mes" options={monthOptions} value={String(month)} onChange={(e) => setMonth(Number(e.target.value))} />
          <Select label="Quincena" options={quincenaOptions} value={String(quincena)} onChange={(e) => setQuincena(Number(e.target.value) as 1 | 2)} />
          <div className="flex items-end">
            <Button size="md" fullWidth onClick={calculate} isLoading={isLoading}>
              Calcular
            </Button>
          </div>
        </div>
      </Card>

      {range && (
        <p className="text-xs text-ink-400">
          Periodo: {range[0]} a {range[1]}. Cada turno usa el factor legal vigente en su propia fecha - una quincena que cruce un cambio de ley se liquida
          correctamente sin ajustes manuales.
        </p>
      )}

      {rows && rows.length === 0 && <EmptyState icon="📋" title="Sin registros en este periodo" description="No hay turnos guardados para la quincena seleccionada." />}

      {rows && rows.length > 0 && (
        <>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              leftIcon={<DownloadIcon width={18} height={18} />}
              onClick={() => range && exportSummaryExcel(rows, range[0], range[1])}
            >
              Descargar Excel (para Contabilidad)
            </Button>
            <Button
              variant="outline"
              leftIcon={<DownloadIcon width={18} height={18} />}
              onClick={() => range && exportSummaryCsv(rows, range[0], range[1])}
            >
              Descargar CSV
            </Button>
          </div>
          <DataTable columns={columns} rows={rows} rowKey={(r) => r.employeeId} isLoading={isLoading} emptyTitle="Sin datos" />
          <Card className="flex items-center justify-between">
            <span className="text-sm font-semibold text-ink-700">Total a pagar (todos los empleados)</span>
            <span className="text-lg font-bold text-ink-900">{formatCurrency(totalPay)}</span>
          </Card>
        </>
      )}
    </div>
  );
}
